/**
 * The host page's own consent decision, read from the `window.dataLayer` we share
 * with it in an inline embed.
 *
 * Inline as `<guest-checkout>` on a venue's own page there is no checkout URL to
 * relay anything on — that URL is the venue's — so the `gtm_consent` hand-off the
 * legacy widget performs never happens. What does exist is the host's own
 * dataLayer, carrying the same four `cookie_consent_*` events their consent
 * platform pushes for their own container. Reading them is how a decision comes to
 * exist for an inline partner journey at all; without one nothing is emitted.
 *
 * Two rules follow from the dataLayer being someone else's:
 *
 * - **It is only ever read.** Entries are never mutated, removed or replayed, and
 *   the host's own `push` keeps running untouched — the observer delegates to it
 *   and returns exactly what it returned, so their container behaves as if we were
 *   not here.
 * - **Only the same document counts.** In an iframe the dataLayer belongs to the
 *   checkout, not to the host, so this path is not used there: the host's decision
 *   arrives on the URL instead. See `docs/consent.md`.
 *
 * A host commonly pushes its decision *after* we load, when the guest answers the
 * banner, so later pushes are observed too. Google Tag Manager replaces
 * `dataLayer.push` with its own implementation when a container loads, which would
 * drop a wrapper installed before it, so the wrapper is re-installed whenever it is
 * found to have been replaced and the array is re-read by index in the same pass.
 */
import type { ConsentCategories } from "../services/cookieConsent/types";
import { isHostConsentEvent, toHostConsentCategories } from "./hostConsent";

/** How often the wrapper is checked and the array re-read. */
const OBSERVE_INTERVAL_MS = 1000;

/**
 * Most consent events kept. Only the latest matter — later events win — so an
 * unusually chatty host cannot grow this without bound.
 */
const MAX_OBSERVED_EVENTS = 50;

type DataLayerEntry = Record<string, unknown>;
type DataLayerPush = (...entries: DataLayerEntry[]) => number;

/** Host consent events seen so far, oldest first. Read, never mutated. */
let observed: DataLayerEntry[] = [];

/** How much of the array has been read, so a re-read only looks at new entries. */
let readTo = 0;

/** The wrapper currently installed, so its replacement can be detected. */
let installed: DataLayerPush | null = null;

/** The `push` the wrapper delegates to. */
let delegate: DataLayerPush | null = null;

let timer: ReturnType<typeof setInterval> | null = null;

/** Everything currently watching, so one read of the dataLayer serves them all. */
const observers = new Set<(categories: ConsentCategories | null) => void>();

/** Whether the checkout shares the host page's document rather than running framed. */
export function isSameDocumentEmbed(): boolean {
  return window.self === window.top;
}

function hostDataLayer(): DataLayerEntry[] | null {
  const layer = window.dataLayer;

  return Array.isArray(layer) ? layer : null;
}

/** Keeps any host consent events among `entries`. Returns whether it kept one. */
function keepConsentEvents(entries: readonly unknown[]): boolean {
  const events = entries.filter(isHostConsentEvent);
  if (events.length === 0) return false;

  observed = [...observed, ...events].slice(-MAX_OBSERVED_EVENTS);

  return true;
}

/** Reads whatever has appeared since the last read. Returns whether anything had. */
function readNewEntries(): boolean {
  const layer = hostDataLayer();
  if (!layer) return false;

  // A host that empties or replaces its dataLayer restarts our position rather
  // than leaving us reading past its end.
  if (layer.length < readTo) readTo = 0;

  const appeared = keepConsentEvents(layer.slice(readTo));
  readTo = layer.length;

  return appeared;
}

/**
 * Wraps the host's `push` so a decision pushed later is observed as it happens.
 *
 * The host's own implementation runs first and its return value is passed
 * straight back, and our own work cannot throw into their call.
 */
function install(layer: DataLayerEntry[], onChange: () => void): void {
  const previous = layer.push as DataLayerPush;
  delegate = previous;

  const wrapper: DataLayerPush = (...entries: DataLayerEntry[]): number => {
    const result = previous.apply(layer, entries);

    try {
      if (keepConsentEvents(entries)) {
        readTo = layer.length;
        onChange();
      }
    } catch (error) {
      console.warn("[Analytics] Failed to read the host page's consent decision:", error);
    }

    return result;
  };

  installed = wrapper;
  layer.push = wrapper;
}

/** Tells everything watching what the host has now said. */
function notify(): void {
  const categories = toHostConsentCategories(observed);

  for (const observer of observers) observer(categories);
}

/** Reads what has appeared, and re-installs the wrapper where it was replaced. */
function rearm(): void {
  const layer = hostDataLayer();
  if (!layer) return;

  const appeared = readNewEntries();
  if (layer.push !== installed) install(layer, notify);
  if (appeared) notify();
}

/** Puts the host's own `push` back, but only where ours is still the one installed. */
function uninstall(): void {
  const layer = hostDataLayer();

  if (layer && delegate && layer.push === installed) layer.push = delegate;

  installed = null;
  delegate = null;
}

/** Stops watching, leaving the host's dataLayer as it was found. */
function stop(): void {
  if (timer) clearInterval(timer);
  timer = null;
  observers.clear();
  uninstall();
}

/**
 * The decision the host page has stated on the dataLayer we share with it.
 *
 * @returns the decision, or `null` when the host has stated nothing recognisable
 * — which the checkout treats exactly as it treats a refusal.
 */
export function readHostDataLayerConsent(): ConsentCategories | null {
  readNewEntries();

  return toHostConsentCategories(observed);
}

/**
 * Watches the host's dataLayer for its consent decision, including one stated
 * after the checkout loaded.
 *
 * The dataLayer is one object, so it is read once and every observer is told what
 * it said: watching is reference counted, and the wrapper and the interval are
 * removed once the last observer has stopped.
 *
 * @param onDecision called with the decision each time the host states something
 * new about it.
 * @returns a teardown that stops this observer watching.
 */
export function observeHostDataLayerConsent(onDecision: (categories: ConsentCategories | null) => void): () => void {
  observers.add(onDecision);

  rearm();
  timer ??= setInterval(rearm, OBSERVE_INTERVAL_MS);

  return () => {
    observers.delete(onDecision);

    if (observers.size === 0) stop();
  };
}

/**
 * Stops watching and forgets what the host said, so a later observer reads the
 * dataLayer afresh.
 *
 * Called when the analytics service is reset — on teardown in a test, and where
 * the checkout reconfigures itself at runtime.
 */
export function resetHostDataLayerConsent(): void {
  stop();
  observed = [];
  readTo = 0;
}
